import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useDispatch, useSelector } from 'react-redux';
import 'react-datepicker/dist/react-datepicker.css';
import { X, Briefcase, Trash2 } from 'lucide-react';
import { fetchUserProfile, updateUserProfile } from '../../redux/AuthSlice/authSlice';

const inputCls = 'w-full rounded-lg border border-[#E6E8EE] bg-[#F8FAFC] px-3 py-2.5 text-[13px] font-medium font-dmsans text-[#0F172A] outline-none placeholder:text-[#94A3B8] focus:border-[#3F57E0] focus:bg-white focus:ring-2 focus:ring-[rgba(63,87,224,0.12)] transition-colors';
const labelCls = 'text-[11px] font-semibold uppercase tracking-[0.06em] text-[#64748B] font-dmsans mb-1.5 block';

const AddExpModal = ({ isOpen, onClose, initialData = null, index = null, onDelete }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        title: '', company: '', startingDate: '', endingDate: '', description: '', stillWorking: false,
    });

    useEffect(() => {
        setForm(initialData
            ? { ...initialData }
            : { title: '', company: '', startingDate: '', endingDate: '', description: '', stillWorking: false }
        );
    }, [initialData, isOpen]);

    const isEdit = index !== null;
    const saveDisabled = loading
        || !form.title
        || !form.company
        || !form.startingDate
        || (!form.endingDate && !form.stillWorking);

    const handleSubmit = () => {
        const entry = {
            title: form.title, company: form.company,
            startingDate: form.startingDate, endingDate: form.endingDate,
            description: form.description, stillWorking: form.stillWorking,
        };
        let payload;
        if (isEdit) {
            const updated = [...(user.experience || [])];
            updated[index] = entry;
            payload = { experience: updated };
        } else {
            payload = { experience: [...(user.experience || []), entry] };
        }
        setLoading(true);
        dispatch(updateUserProfile(payload))
            .then(() => { setLoading(false); onClose(); dispatch(fetchUserProfile()); })
            .catch(() => setLoading(false));
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
            style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)' }}
        >
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg mx-0 sm:mx-4 overflow-hidden">

                {/* ── Header ─────────────────────────────────── */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-[#EFF1F5]">
                    <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: '#EEF1FE' }}
                    >
                        <Briefcase size={15} style={{ color: '#3F57E0' }} />
                    </div>
                    <div className="flex-1 text-left">
                        <div className="text-[14px] font-bold text-[#0F172A] font-dmsans leading-tight">
                            {isEdit ? 'Edit work experience' : 'Add work experience'}
                        </div>
                        <div className="text-[11.5px] text-[#94A3B8] font-dmsans mt-0.5">
                            {isEdit ? 'Update the details for this role' : 'Add a role to your profile'}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-[#94A3B8] hover:bg-[#F4F5F8] hover:text-[#334155] transition-colors"
                    >
                        <X size={15} />
                    </button>
                </div>

                {/* ── Body ───────────────────────────────────── */}
                <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">

                    {/* Title + Company */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>
                                Job title <span className="text-red-400">*</span>
                            </label>
                            <input
                                className={inputCls}
                                placeholder="e.g. Product Manager"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                maxLength={50}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>
                                Company <span className="text-red-400">*</span>
                            </label>
                            <input
                                className={inputCls}
                                placeholder="e.g. Google"
                                value={form.company}
                                onChange={(e) => setForm({ ...form, company: e.target.value })}
                                maxLength={50}
                            />
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>
                                Start date <span className="text-red-400">*</span>
                            </label>
                            <DatePicker
                                selected={form.startingDate ? new Date(form.startingDate) : null}
                                onChange={(date) => setForm({ ...form, startingDate: date })}
                                dateFormat="MMM yyyy"
                                showMonthYearPicker
                                placeholderText="Month, Year"
                                wrapperClassName="w-full"
                                className={inputCls}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>
                                End date {!form.stillWorking && <span className="text-red-400">*</span>}
                            </label>
                            <DatePicker
                                selected={form.endingDate ? new Date(form.endingDate) : null}
                                onChange={(date) => setForm({ ...form, endingDate: date })}
                                dateFormat="MMM yyyy"
                                showMonthYearPicker
                                placeholderText={form.stillWorking ? 'Present' : 'Month, Year'}
                                disabled={form.stillWorking || !form.startingDate}
                                minDate={form.startingDate ? new Date(form.startingDate) : null}
                                wrapperClassName="w-full"
                                className={`${inputCls} ${form.stillWorking ? 'opacity-50 cursor-not-allowed' : ''}`}
                            />
                        </div>
                    </div>

                    {/* Still working toggle */}
                    <label className="flex items-center gap-2.5 cursor-pointer select-none w-fit">
                        <div
                            onClick={() => setForm({ ...form, stillWorking: !form.stillWorking, endingDate: !form.stillWorking ? null : form.endingDate })}
                            className="relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0"
                            style={{ background: form.stillWorking ? '#3F57E0' : '#E2E8F0' }}
                        >
                            <span
                                className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200"
                                style={{ transform: form.stillWorking ? 'translateX(16px)' : 'translateX(0)' }}
                            />
                        </div>
                        <span className="text-[12.5px] font-medium text-[#334155] font-dmsans">I currently work here</span>
                    </label>

                    {/* Description */}
                    <div>
                        <label className={labelCls}>Description</label>
                        <textarea
                            className={`${inputCls} resize-none`}
                            placeholder="Describe your responsibilities and achievements…"
                            rows={3}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            maxLength={1000}
                        />
                        <div className="text-right text-[10.5px] text-[#94A3B8] font-dmsans mt-1">
                            {(form.description || '').length}/1000
                        </div>
                    </div>
                </div>

                {/* ── Footer ─────────────────────────────────── */}
                <div className="px-5 py-3.5 border-t border-[#EFF1F5] flex items-center gap-2">
                    {isEdit && onDelete && (
                        <button
                            type="button"
                            onClick={onDelete}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold font-dmsans text-red-500 hover:bg-red-50 border border-red-100 transition-colors"
                        >
                            <Trash2 size={12} /> Delete
                        </button>
                    )}
                    <div className="flex-1" />
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-[#E6E8EE] bg-white text-[12.5px] font-semibold font-dmsans text-[#334155] hover:bg-[#F4F5F8] transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saveDisabled}
                        className="px-4 py-2 rounded-lg text-[12.5px] font-semibold font-dmsans text-white transition-opacity disabled:opacity-40 hover:opacity-90"
                        style={{ background: '#3F57E0' }}
                    >
                        {loading ? 'Saving…' : isEdit ? 'Save changes' : 'Add experience'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddExpModal;
